import React, { useState } from "react";
import { useParams } from "react-router-dom";
import { API } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Heart, Search, Loader2 } from "lucide-react";

const STATUS_STYLE = {
  recebida: "bg-copaiba-500 text-forest-900",
  em_analise: "bg-copaiba-300 text-forest-900",
  aprovada: "bg-forest-700 text-cream-100",
  indeferida: "bg-terracotta-500 text-cream-100",
  concluida: "bg-forest-900 text-cream-100",
};

export default function SocialTrackPage() {
  const { protocol: paramProto } = useParams();
  const [protocol, setProtocol] = useState((paramProto || "").toUpperCase());
  const [cpf, setCpf] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e?.preventDefault();
    setError(""); setData(null);
    const p = protocol.trim().toUpperCase();
    const c = cpf.replace(/\D/g, "");
    if (!p) { setError("Informe o número do protocolo."); return; }
    if (c.length < 4) { setError("Informe pelo menos os últimos 4 dígitos do CPF."); return; }
    setLoading(true);
    try {
      const res = await fetch(`${API}/social/track/${encodeURIComponent(p)}?cpf=${c}`);
      const j = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(j.detail || "Não foi possível consultar.");
      setData(j);
    } catch (err) { setError(err.message); }
    finally { setLoading(false); }
  };

  return (
    <div className="container mx-auto px-4 sm:px-6 py-10 max-w-2xl" data-testid="social-track-page">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-12 h-12 rounded-full bg-forest-700 text-cream-100 flex items-center justify-center">
          <Heart className="w-5 h-5" />
        </div>
        <div>
          <div className="text-xs uppercase tracking-[0.18em] text-forest-700">Setor Social · COOMFLONA</div>
          <h1 className="font-display text-3xl md:text-4xl text-forest-900 leading-tight">Acompanhar solicitação</h1>
        </div>
      </div>

      <form onSubmit={submit} className="bg-white border border-cream-400 p-5 mb-5 space-y-4" data-testid="track-form">
        <label className="block">
          <span className="text-xs uppercase tracking-[0.18em] text-forest-900/70 font-medium">Número do protocolo *</span>
          <Input value={protocol} onChange={(e) => setProtocol(e.target.value.toUpperCase())}
            placeholder="COOMFLONA-DDMMYYYY-NNNN" data-testid="track-protocol" required className="mt-2 font-mono" />
        </label>
        <label className="block">
          <span className="text-xs uppercase tracking-[0.18em] text-forest-900/70 font-medium">Últimos 4 dígitos do CPF *</span>
          <Input value={cpf} onChange={(e) => setCpf(e.target.value.replace(/\D/g, "").slice(0, 11))}
            placeholder="Ex.: 4321" data-testid="track-cpf" required inputMode="numeric" className="mt-2" />
        </label>
        <Button type="submit" disabled={loading} data-testid="track-submit"
          className="w-full bg-forest-700 hover:bg-forest-900 text-cream-100 rounded-none py-6 text-base">
          {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Consultando...</> :
                     <><Search className="w-4 h-4 mr-2" /> Consultar situação</>}
        </Button>
      </form>

      {error && (
        <div className="bg-terracotta-500/10 border border-terracotta-500 p-4 mb-5 text-terracotta-500" data-testid="track-error">
          {error}
        </div>
      )}

      {data && (
        <div className="bg-cream-100 border border-forest-700/40 p-5" data-testid="track-result">
          <div className="text-xs uppercase tracking-[0.18em] text-forest-700">Protocolo</div>
          <div className="font-mono text-lg text-forest-900 mb-3">{data.protocol}</div>

          <div className="mb-3">
            <span className={`inline-block text-xs uppercase tracking-[0.18em] px-3 py-1 ${STATUS_STYLE[data.status] || "bg-cream-400"}`}>
              {data.status_label}
            </span>
          </div>

          {data.status_note && (
            <div className="text-sm text-forest-900/85 bg-white border border-cream-400 p-3 mb-3">
              <b>Observação:</b> {data.status_note}
            </div>
          )}

          <div className="text-sm text-forest-900/85 space-y-1 mb-4">
            <div><b>Solicitante:</b> {data.name}</div>
            {data.community && <div><b>Comunidade:</b> {data.community}</div>}
            {data.request_type && <div><b>Tipo:</b> {data.request_type}</div>}
            {data.created_at_human && <div><b>Registrada em:</b> {data.created_at_human}</div>}
          </div>

          {Array.isArray(data.history) && data.history.length > 0 && (
            <div>
              <div className="text-xs uppercase tracking-[0.18em] text-forest-700 mb-2">Histórico</div>
              <ul className="space-y-2">
                {data.history.slice().reverse().map((h, i) => (
                  <li key={i} className="bg-white border border-cream-400 p-2 text-xs">
                    <span className="text-forest-700 font-medium">{new Date(h.at).toLocaleString("pt-BR")}</span>
                    <span className="mx-2">·</span>
                    <span>{h.status_label}</span>
                    {h.note && <div className="mt-1 text-forest-900/80">{h.note}</div>}
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="mt-5 text-xs text-forest-900/70">
            Dúvidas? Fale com {data.responsavel?.nome || "o Setor Social"} pelo WhatsApp {data.responsavel?.whatsapp_raw || ""}.
          </div>
        </div>
      )}
    </div>
  );
}
